"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { 
  Dumbbell, 
  Globe, 
  KanbanSquare, 
  RotateCcw, 
  ArrowLeft, 
  Sparkles,
  CheckCircle2
} from "lucide-react";

interface GymTopBarProps {
  currency: "INR" | "USD";
  onCurrencyChange: (c: "INR" | "USD") => void;
}

export default function GymTopBar({ currency, onCurrencyChange }: GymTopBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [resetting, setResetting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const isCrm = pathname.includes("/crm");

  const handleReset = async () => {
    try {
      setResetting(true);
      const res = await fetch("/api/demos/gym/reset", { method: "POST" });
      if (res.ok) {
        setToast("Demo member leads reset to initial state!");
        setTimeout(() => {
          setToast(null);
          router.refresh();
          window.location.reload();
        }, 1200);
      }
    } catch (err) {
      console.error("Failed to reset gym demo:", err);
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-stone-900 border border-amber-500/50 text-white px-4 py-2.5 rounded-xl shadow-2xl flex items-center gap-2.5 text-xs font-semibold animate-in fade-in slide-in-from-top-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{toast}</span>
        </div>
      )}

      <div className="bg-stone-950/95 backdrop-blur-md border-b border-stone-800 text-stone-200 sticky top-0 z-40 px-3 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
        {/* Left: Back & Badge */}
        <div className="flex items-center gap-3">
          <Link
            href="/demos"
            className="flex items-center gap-1.5 text-stone-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <span className="font-medium hidden sm:inline">All Demos</span>
          </Link>

          <span className="text-stone-700 hidden sm:inline">•</span>

          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Vertical Demo
            </span>
            <span className="font-bold text-white tracking-tight flex items-center gap-1.5">
              <Dumbbell className="w-3.5 h-3.5 text-amber-400" />
              Iron &amp; Pulse Athletic Club
            </span>
          </div>
        </div>

        {/* Center / Right: Website vs CRM Toggle */}
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="bg-stone-900/90 p-1 rounded-xl border border-stone-800 flex items-center gap-1 shadow-inner">
            <Link
              href="/demos/gym"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-medium transition-all ${
                !isCrm
                  ? "bg-amber-500 text-stone-950 font-bold shadow-md"
                  : "text-stone-400 hover:text-white"
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              <span>Client Website</span>
            </Link>

            <Link
              href="/demos/gym/crm"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-medium transition-all ${
                isCrm
                  ? "bg-amber-500 text-stone-950 font-bold shadow-md"
                  : "text-stone-400 hover:text-white"
              }`}
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              <span>Floor &amp; Member CRM</span>
            </Link>
          </div>

          {/* Currency Toggle */}
          <div className="flex items-center bg-stone-900 border border-stone-800 rounded-lg p-0.5 text-[11px] font-bold">
            <button
              onClick={() => onCurrencyChange("INR")}
              className={`px-2 py-1 rounded transition-colors ${
                currency === "INR" ? "bg-amber-500 text-stone-950" : "text-stone-400 hover:text-white"
              }`}
            >
              ₹ INR
            </button>
            <button
              onClick={() => onCurrencyChange("USD")}
              className={`px-2 py-1 rounded transition-colors ${
                currency === "USD" ? "bg-amber-500 text-stone-950" : "text-stone-400 hover:text-white"
              }`}
            >
              $ USD
            </button>
          </div>

          {/* Reset Demo Button */}
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-stone-900 hover:bg-stone-800 border border-stone-800 text-stone-400 hover:text-white transition-colors text-[11px] font-semibold disabled:opacity-50"
            title="Reset gym test leads to initial state"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${resetting ? "animate-spin text-amber-400" : ""}`} />
            <span className="hidden md:inline">Reset Demo</span>
          </button>
        </div>
      </div>
    </>
  );
}
